import React from "react";
import { connect } from "react-redux";
import { getAssetNames } from "actions/asset";
import { getPortfolios } from "actions/portfolio";
import { getSubscriptions, updateSubscriptions } from "actions/subscription";
import { getUserByToken } from "actions/user";
import cx from "classnames";
import difference from "lodash/difference";
import sortBy from "lodash/sortBy";
import PropTypes from "prop-types";
import { isInactiveUserSubscriptions } from "selectors/location";

import Card, { CardBlock } from "components/Card";
import Checkbox from "components/Checkbox";
import { ConfirmDialog } from "components/Dialog";
import styles from "components/Settings/Settings.css";
import SubmitButton from "components/SubmitButton";
import TitledMultiDropdown from "components/TitledMultiDropdown";
import { DataStorage, Translate } from "utils";
import Restricted from "utils/Restricted";
import { getUrlParams } from "utils/urlParams";

const { t } = new Translate("Settings");

export class Subscriptions extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isConfirmModalOpen: false,
      unsubscribeDeals: false,
      assetDigestSubscriptions: props.assetDigestSubscriptions,
      dealDigestSubscriptions: props.dealDigestSubscriptions,
      portfolioDealSubscriptions: props.portfolioDealSubscriptions,
    };

    this.handleSave = this.handleSave.bind(this);
    this.onSaveSubscriptions = this.onSaveSubscriptions.bind(this);
    this.unsubscribeDeals = this.unsubscribeDeals.bind(this);
  }

  componentDidMount() {
    const { email } = getUrlParams();
    const token = getUrlParams().invite_token;
    if (token && email) {
      this.props.getUser(email, token).then(() => {
        this.props.getSubscriptions();
        this.props.getAssetNames();
        this.props.getPortfolios();
        window.location.search = "";
      });
    } else {
      this.props.getSubscriptions();
      this.props.getAssetNames();
      this.props.getPortfolios();
    }
  }

  componentWillReceiveProps(props) {
    if (
      this.state.assetDigestSubscriptions.length < 1 &&
      this.state.dealDigestSubscriptions.length < 1 &&
      this.state.portfolioDealSubscriptions.length < 1
    ) {
      this.setState({
        assetDigestSubscriptions: props.assetDigestSubscriptions,
        dealDigestSubscriptions: props.dealDigestSubscriptions,
        portfolioDealSubscriptions: props.portfolioDealSubscriptions,
      });
    }
  }

  onSaveSubscriptions() {
    const { unsubscribeDeals } = this.state;
    this.setState({ isConfirmModalOpen: false });
    this.props.onSaveSubscriptions(
      unsubscribeDeals,
      this.state.assetDigestSubscriptions.map((s) => ({ assetID: s.asset.id })),
      this.state.dealDigestSubscriptions.map((s) => ({
        portfolioID: s.portfolio.id,
      })),
      this.state.portfolioDealSubscriptions.map((s) => ({
        portfolioID: s.portfolio.id,
      }))
    );
  }

  handleSave() {
    if (
      difference(
        this.props.portfolioDealSubscriptions.map((s) => s.portfolio.id),
        this.state.portfolioDealSubscriptions.map((s) => s.portfolio.id)
      ).length
    ) {
      this.setState({ isConfirmModalOpen: true });
    } else {
      this.onSaveSubscriptions();
    }
  }

  unsubscribeDeals() {
    this.setState({ unsubscribeDeals: !this.state.unsubscribeDeals });
  }

  componentWillUnMount() {
    // eslint-disable-line
    DataStorage.clear();
  }

  render() {
    const { assetNames, isStandalone, portfolioNames } = this.props;
    const {
      assetDigestSubscriptions,
      dealDigestSubscriptions,
      isConfirmModalOpen,
      portfolioDealSubscriptions,
      unsubscribeDeals,
    } = this.state;

    return (
      <>
        {isConfirmModalOpen && (
          <ConfirmDialog
            confirmLabel={t("save")}
            onCancel={() => this.setState({ isConfirmModalOpen: false })}
            onConfirm={this.onSaveSubscriptions}
          >
            <div>{t("Subscriptions.unsubscribeDeals.body")}</div>
            <br />
            <Checkbox
              isChecked={unsubscribeDeals}
              label={t("Subscriptions.unsubscribeDeals.unsubscribe")}
              onChange={this.unsubscribeDeals}
            />
          </ConfirmDialog>
        )}
        <Card
          className={cx(styles.container, {
            [styles.standalone]: isStandalone,
          })}
        >
          <Restricted requiredPermissions="assets">
            <CardBlock className={styles.subtitle}>
              {t("Subscriptions.assetDigest.title")}
            </CardBlock>
            <CardBlock className={styles.wide}>
              <TitledMultiDropdown
                keyExtractor={(asset) => asset.id}
                limit={3}
                onSelect={(assets) =>
                  this.setState({
                    assetDigestSubscriptions: assets.map((asset) => ({
                      asset,
                    })),
                  })
                }
                selectedValues={assetDigestSubscriptions.map((s) => s.asset)}
                title={t("Subscriptions.assetDigest.description")}
                valueExtractor={(asset) => asset.name}
                values={assetNames}
                isEdit
              />
            </CardBlock>
          </Restricted>
          <Restricted requiredPermissions="deals">
            <CardBlock className={styles.subtitle}>{t("Subscriptions.dealDigest.title")}</CardBlock>
            <CardBlock className={styles.wide}>
              <TitledMultiDropdown
                keyExtractor={(portfolio) => portfolio.id}
                limit={3}
                onSelect={(portfolios) =>
                  this.setState({
                    dealDigestSubscriptions: portfolios.map((portfolio) => ({
                      portfolio,
                    })),
                  })
                }
                selectedValues={dealDigestSubscriptions.map((s) => s.portfolio)}
                title={t("Subscriptions.dealDigest.description")}
                valueExtractor={(portfolio) => portfolio.name}
                values={portfolioNames}
                isEdit
              />
            </CardBlock>
            <CardBlock className={styles.subtitle}>
              {t("Subscriptions.portfolio_deal.title")}
            </CardBlock>
            <CardBlock className={styles.wide}>
              <TitledMultiDropdown
                keyExtractor={(portfolio) => portfolio.id}
                limit={3}
                onSelect={(portfolios) =>
                  this.setState({
                    portfolioDealSubscriptions: portfolios.map((portfolio) => ({
                      portfolio,
                    })),
                  })
                }
                selectedValues={portfolioDealSubscriptions.map((s) => s.portfolio)}
                title={t("Subscriptions.portfolio_deal.description")}
                valueExtractor={(portfolio) => portfolio.name}
                values={portfolioNames}
                isEdit
              />
            </CardBlock>
          </Restricted>
          <CardBlock className={styles.footer}>
            <SubmitButton
              isSubmitting={this.props.isLoading}
              onSubmit={this.handleSave}
              submitLabel={t("save")}
              submittingLabel={t("saving")}
            />
          </CardBlock>
        </Card>
      </>
    );
  }
}

Subscriptions.propTypes = {
  isLoading: PropTypes.bool,
  isStandalone: PropTypes.bool,
  getSubscriptions: PropTypes.func.isRequired,
  getAssetNames: PropTypes.func.isRequired,
  getPortfolios: PropTypes.func.isRequired,
  getUser: PropTypes.func.isRequired,
  onSaveSubscriptions: PropTypes.func.isRequired,
  assetDigestSubscriptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      asset: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    })
  ),
  dealDigestSubscriptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      portfolio: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    })
  ),
  portfolioDealSubscriptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      portfolio: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    })
  ),
  assetNames: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  portfolioNames: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
};

const mapStateToProps = (state) => ({
  assetNames: state.assetNames,
  assetDigestSubscriptions: sortBy(state.assetDigestSubscriptions, ["asset.name"]),
  dealDigestSubscriptions: sortBy(state.dealDigestSubscriptions, ["portfolio.name"]),
  portfolioDealSubscriptions: sortBy(state.portfolioDealSubscriptions, ["portfolio.name"]),
  isLoading: state.loading.user,
  isStandalone: isInactiveUserSubscriptions(state),
  portfolioNames: state.portfolios,
});

const mapDispatchToProps = (dispatch) => ({
  onSaveSubscriptions: (unsubscribeDeals, assetDigest, dealDigest, portfolioDeal) =>
    dispatch(updateSubscriptions(unsubscribeDeals, assetDigest, dealDigest, portfolioDeal)),
  getSubscriptions: () => dispatch(getSubscriptions()),
  getAssetNames: () => dispatch(getAssetNames()),
  getPortfolios: () => dispatch(getPortfolios()),
  getUser: (email, token) => dispatch(getUserByToken(email, token)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Subscriptions);
